import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Snackbar
} from '@mui/material';
import {
  Save,
  Telegram,
  Email,
  Phone,
  LocationOn,
  School,
  Work,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  Psychology as AIIcon,
  Backup as BackupIcon
} from '@mui/icons-material';
import { useAppStore } from '../stores/useAppStore';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';
import { canManageAIKnowledge, canManageBackups } from '../utils/rbac';


export const UserSettingsPage: React.FC = () => {
  const { user, updateUser } = useAppStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    address: '',
    school: '',
    position: '',
    telegramEnabled: false,
    telegramChatId: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        address: user.address || '',
        school: user.school || '',
        position: user.position || '',
        telegramEnabled: user.telegramEnabled || false,
        telegramChatId: user.telegramChatId || ''
      });
    }
  }, [user]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setMessage(null);

    try {
      // Call API to update user
      const response = await apiService.put<{ success: boolean; user: any }>('/api/users/me', {
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || undefined,
        address: formData.address || undefined,
        school: formData.school || undefined,
        position: formData.position || undefined,
        telegramEnabled: formData.telegramEnabled,
        telegramChatId: formData.telegramChatId || undefined
      });

      if (response.success && response.user) {
        // Update user in store
        updateUser({
          ...user,
          ...response.user
        });

        setMessage({ type: 'success', text: 'Cập nhật thông tin thành công!' });
        setSnackbarMessage('Cập nhật thông tin thành công!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Navigate to profile after a short delay to show the toast
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } else {
        throw new Error('Cập nhật thông tin không thành công');
      }
    } catch (error) {
      logger.error('Error updating user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi cập nhật thông tin.';
      setMessage({ type: 'error', text: errorMessage });
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleBack = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Cài đặt tài khoản
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        QL hệ thống, Sao lưu và Chatbot
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>

        {/* Admin Settings - Only for super admin */}
        {user?.isSuperAdmin && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quản lý hệ thống
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<StorageIcon />}
                  onClick={() => navigate('/admin/tenants')}
                  sx={{ mt: 2 }}
                >
                  Quản lý thư viện
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* AI Knowledge Management - Expanded Access */}
        {user && canManageAIKnowledge(user.roles) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Trí tuệ nhân tạo (AI)
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AIIcon />}
                  onClick={() => navigate('/settings/ai-knowledge')}
                  sx={{ mt: 2, background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}
                >
                  Quản lý Tri thức AI
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Cập nhật dữ liệu để Chatbot thông minh hơn.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Backup Management - Expanded Access */}
        {user && canManageBackups(user.roles) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Hệ thống Sao lưu
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BackupIcon />}
                  onClick={() => navigate('/backups')}
                  sx={{ mt: 2 }}
                >
                  Quản lý Sao lưu (Backups)
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Tạo và phục hồi dữ liệu hệ thống.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notification Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cài đặt thông báo
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.telegramEnabled}
                      onChange={handleSwitchChange('telegramEnabled')}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Telegram sx={{ mr: 1 }} />
                      <Typography>Nhận thông báo qua Telegram</Typography>
                    </Box>
                  }
                />

                {formData.telegramEnabled && (
                  <TextField
                    fullWidth
                    label="Telegram Chat ID"
                    value={formData.telegramChatId}
                    onChange={handleInputChange('telegramChatId')}
                    margin="normal"
                    helperText="Nhập Chat ID của bạn trên Telegram để nhận thông báo"
                    placeholder="@username hoặc chat_id"
                  />
                )}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Thông báo sẽ bao gồm:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Nộp giáo án mới<br />
                  • Duyệt/từ chối giáo án<br />
                  • Mượn/trả sách<br />
                  • Thông báo hệ thống
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Quay lại trang cá nhân
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettingsPage;
